import './highlight.scss'

import React, { Fragment } from 'react'

import Button, { type ButtonProps } from '@/components/button/button'
import Image from '@/components/image/image'
import Link from '@/components/link/link'
import TitleLink from '@/components/title-link/title-link'
import useDefaultImages from '@/hooks/useDefaultImages'
import { generateUrl } from '@/internal/utils/generate-url'

export type HighlightProps = {
  __typename:
    | 'DatoCmsPctPost'
    | 'DatoCmsPctProject'
    | 'DatoCmsPctPage'
    | 'DatoCmsPctPaper'
    | 'DatoCmsPctDialog'
    | 'DatoCmsPctEpisode'
    | 'DatoCmsPctExternalContent'
  headline: string | null
  topic?: Queries.TopicReferenceFragment | null
  title: string | null
  excerpt: Queries.Maybe<string>
  persons:
    | (
        | {
            readonly __typename: 'DatoCmsGctPerson'
            readonly firstName: string | null
            readonly secondName: string | null
            readonly job: string | null
          }
        | undefined
        | null
      )[]
    | undefined
    | null
  buttons: ButtonProps[]
  link: string
  image: Queries.imgHighlightBasicFragment | null
}

const Highlight: React.FC<HighlightProps> = ({
  __typename,
  headline,
  topic,
  title,
  excerpt,
  persons,
  buttons,
  link,
  image
}) => {
  const { imgHighlightBasic } = useDefaultImages(__typename) ?? {}
  return (
    <div className="highlight">
      <div className="container">
        <div className="row">
          <div className="col">
            <h2>{headline}</h2>
          </div>
        </div>
        <div className="row">
          <div className="col-lg-4 order-2 order-lg-1 mt-4 mt-lg-0">
            {topic?.slug && (
              <div className="topic">
                <Button
                  to={generateUrl('DatoCmsPctTopic', topic?.slug)}
                  title={topic?.externalName}
                  color="tertiary"
                />
              </div>
            )}
            <TitleLink htmltag="h3" to={link} title={title ?? ''} />

            <div className="description">{excerpt}</div>
            <div className="persons">
              {persons?.map(
                (item, index) =>
                  item && (
                    <Fragment key={`speaker-${index}`}>
                      <p className="name">
                        {item.firstName} {item.secondName}
                      </p>
                      <p>{item.job}</p>
                    </Fragment>
                  )
              )}
            </div>
            <div className="links">
              {buttons.map((button, index) => (
                <Button
                  key={button.to}
                  to={button.to}
                  title={button.title}
                  color={index % 2 === 0 ? 'primary' : 'secondary'}
                />
              ))}
            </div>
          </div>
          <div className="col-lg-8 order-1 order-lg-2">
            <div className="media">
              <Link to={link}>
                <Image
                  image={image?.gatsbyImageData ?? imgHighlightBasic?.desktop}
                  alt={title ?? ''}
                />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default Highlight
